/**
 * Starting points. Each is a complete, runnable graph that shows one shape
 * the platform supports; the person swaps in their own connection and fields.
 */

import type { WfEdge, WfNode } from './types'

export interface Template {
  key: string
  name: string
  description: string
  shape: string
  tags: string[]
  variables?: Record<string, unknown>
  nodes: WfNode[]
  edges: WfEdge[]
}

// The graphs below were sketched left to right in 320px columns and 180px
// branch rows; the canvas flows top to bottom, so columns become rows.
const at = (col: number, row: number) => ({ x: 120 + Math.round((row - 160) * 1.7), y: 60 + Math.round((col - 60) * 0.45) })

export const TEMPLATES: Template[] = [
  {
    key: 'app-event-filter-notify',
    name: 'App event → filter → notify',
    description: 'Watch a connected app for new items, keep the ones that matter, and post each to a URL.',
    shape: 'app event → filter → for each → http',
    tags: ['app', 'rpa'],
    nodes: [
      { id: 'watch', type: 'trigger.app', name: 'New items', params: { connector_id: '', connection_id: '', action_id: '', input: {}, minutes: 5, key: 'id', first_poll: 'skip', sample: '{"id": "abc-1", "status": "new"}' }, position: at(60, 160) },
      { id: 'keep', type: 'data.filter', name: 'Only the ones that matter', params: { condition: '$json.status == "new"' }, position: at(380, 160) },
      { id: 'each', type: 'flow.for_each', name: 'For each item', params: { source: 'items', batch_size: 1 }, position: at(700, 160) },
      { id: 'post', type: 'http.request', name: 'Notify', params: { method: 'POST', url: 'https://example.com/notify', body: '{ "id": "{{ $json.id }}" }', fail_on_error: true }, position: at(1020, 80), on_error: 'continue', retries: 2 },
      { id: 'sum', type: 'data.aggregate', name: 'Summary', params: { key: 'notified' }, position: at(1020, 300) },
    ],
    edges: [
      { id: 'e1', source: 'watch', source_port: 'main', target: 'keep' },
      { id: 'e2', source: 'keep', source_port: 'main', target: 'each' },
      { id: 'e3', source: 'each', source_port: 'loop', target: 'post' },
      { id: 'e4', source: 'each', source_port: 'done', target: 'sum' },
    ],
  },
  {
    key: 'webhook-branch',
    name: 'Webhook → branch → respond',
    description: 'Receive a request, route it by a field, and answer the caller with what you decided.',
    shape: 'webhook → if → respond',
    tags: ['webhook'],
    nodes: [
      { id: 'hook', type: 'trigger.webhook', name: 'Incoming request', params: { path: 'orders', method: 'POST', respond: 'when_done', sample: '{"method": "POST", "query": {}, "headers": {}, "body": {"id": "A-1042", "total": 1800}}' }, position: at(60, 160) },
      { id: 'big', type: 'flow.if', name: 'Large order?', params: { condition: '($json.body.total or 0) > 1000' }, position: at(380, 160) },
      { id: 'yes', type: 'data.set', name: 'Flag for review', params: { fields: [{ key: 'route', value: 'review' }, { key: 'reason', value: 'Total {{ $json.body.total }} exceeds 1000' }] }, position: at(700, 80) },
      { id: 'no', type: 'data.set', name: 'Auto-approve', params: { fields: [{ key: 'route', value: 'approved' }] }, position: at(700, 260) },
      { id: 'join', type: 'flow.merge', name: 'Either way', params: {}, position: at(1020, 160) },
      { id: 'reply', type: 'output.respond', name: 'Answer the caller', params: { mode: 'first', status: 200 }, position: at(1340, 160) },
    ],
    edges: [
      { id: 'e1', source: 'hook', source_port: 'main', target: 'big' },
      { id: 'e2', source: 'big', source_port: 'true', target: 'yes' },
      { id: 'e3', source: 'big', source_port: 'false', target: 'no' },
      { id: 'e4', source: 'yes', source_port: 'main', target: 'join' },
      { id: 'e5', source: 'no', source_port: 'main', target: 'join' },
      { id: 'e6', source: 'join', source_port: 'main', target: 'reply' },
    ],
  },
  {
    key: 'schedule-connector-loop',
    name: 'Schedule → connector → for each',
    description: 'Every 15 minutes, pull rows from a connected app, filter them, and do something per row.',
    shape: 'schedule → action → filter → for each → http',
    tags: ['schedule', 'connector'],
    nodes: [
      { id: 'tick', type: 'trigger.schedule', name: 'Every 15 minutes', params: { mode: 'interval', minutes: 15 }, position: at(60, 160) },
      { id: 'fetch', type: 'connector.action', name: 'Fetch rows', params: { connection_id: '', action_id: '', input: {}, mode: 'once' }, position: at(380, 160) },
      { id: 'keep', type: 'data.filter', name: 'Only new ones', params: { condition: '$json.status == "new"' }, position: at(700, 160) },
      { id: 'each', type: 'flow.for_each', name: 'For each row', params: { source: 'items', batch_size: 1 }, position: at(1020, 160) },
      { id: 'post', type: 'http.request', name: 'Notify', params: { method: 'POST', url: 'https://example.com/notify', body: '{ "id": "{{ $json.id }}", "n": {{ $loop.index }} }', fail_on_error: true }, position: at(1340, 80) },
      { id: 'sum', type: 'data.aggregate', name: 'Summary', params: { key: 'notified' }, position: at(1340, 300) },
    ],
    edges: [
      { id: 'e1', source: 'tick', source_port: 'main', target: 'fetch' },
      { id: 'e2', source: 'fetch', source_port: 'main', target: 'keep' },
      { id: 'e3', source: 'keep', source_port: 'main', target: 'each' },
      { id: 'e4', source: 'each', source_port: 'loop', target: 'post' },
      { id: 'e5', source: 'each', source_port: 'done', target: 'sum' },
    ],
  },
  {
    key: 'code-while',
    name: 'Code, while, and a function call',
    description: 'Paginate with a while loop, transform with code, and hand the result to another workflow.',
    shape: 'manual → while(code) → call workflow',
    tags: ['code', 'loops'],
    variables: { page_size: 25 },
    nodes: [
      { id: 'start', type: 'trigger.manual', name: 'Start', params: { sample: '{"page": 0, "has_more": true, "rows": []}' }, position: at(60, 160) },
      { id: 'more', type: 'flow.while', name: 'While there are pages', params: { condition: '$json.has_more and $loop.index < 5', max_iterations: 50, feed_back: true }, position: at(380, 160) },
      {
        id: 'page',
        type: 'data.code',
        name: 'Fetch a page',
        params: {
          mode: 'per_item',
          code: [
            '# Pretend to fetch one page; replace with an HTTP or connector node.',
            'page = $json.page',
            'size = $vars.page_size',
            'rows = [{"id": page * size + i} for i in range(size)]',
            'return {',
            '    "page": page + 1,',
            '    "has_more": page + 1 < 3,',
            '    "rows": $json.rows + rows,',
            '}',
          ].join('\n'),
        },
        position: at(700, 80),
      },
      { id: 'total', type: 'data.code', name: 'Count everything', params: { mode: 'all', code: 'last = $items[-1] if $items else {"rows": []}\nreturn {"pages": len($items), "rows": len(last["rows"])}' }, position: at(700, 300) },
      { id: 'fn', type: 'flow.call', name: 'Hand off', params: { workflow_id: '', mode: 'once' }, position: at(1020, 300), disabled: true },
    ],
    edges: [
      { id: 'e1', source: 'start', source_port: 'main', target: 'more' },
      { id: 'e2', source: 'more', source_port: 'loop', target: 'page' },
      { id: 'e3', source: 'more', source_port: 'done', target: 'total' },
      { id: 'e4', source: 'total', source_port: 'main', target: 'fn' },
    ],
  },
]
